import React, { useEffect } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useState } from "react";
import { gql } from "@apollo/client";
import Typography from "../typography";
import { Grid, FormControlLabel, Switch } from "@material-ui/core";
import CheckedIcon from "@material-ui/icons/CheckCircleOutline";
import UnCheckedIcon from "@material-ui/icons/HighlightOffOutlined";
import CalendarComponent from "./CalendarComponent";
import moment from "moment";
import NextMatchCard from "./NexMatchCard";
import { injectIntl } from "react-intl";
import messages from "./PageDashboard.intl";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import {
  DashboardCard,
  DashboardCardHeader,
  DashboardCardTitle,
  DashboardCardBody,
  DashboardBadge,
  PlayerRow,
  EmptyText,
} from "./dashboardCards";

const NEXT_MATCH = gql`
  query NextMatch($teamId: ID!) {
    nextMatch(teamId: $teamId) {
      id
      date
      homeTeam {
        name
      }
      isHomeTeamLocal
      awayTeam
      location
      time
      status
      playersConfirmed {
        id
      }
    }
  }
`;

const ATTEND_MATCH = gql`
  mutation AttendMatch($matchId: ID!, $playerId: ID!, $isComing: Boolean!) {
    attendMatch(matchId: $matchId, playerId: $playerId, isComing: $isComing) {
      date
      playersConfirmed {
        id
      }
    }
  }
`;

const SectionWrapper = styled("div")(({ theme }: IStyledArguments) => ({
  marginBottom: theme.spacing(3),
}));

const AttendanceSection = styled(DashboardCard)(
  ({ theme }: IStyledArguments) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  })
);

const AttendanceTitle = styled(Typography.H6)(({ theme }: IStyledArguments) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(2),
}));

const NextMatchSection = styled(DashboardCard)(
  ({ theme }: IStyledArguments) => ({
    marginBottom: theme.spacing(3),
  })
);

const MatchHeader = DashboardCardHeader;

const MatchHeaderTitle = DashboardCardTitle;

const MatchDateBadge = DashboardBadge;

const MatchBody = DashboardCardBody;

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(2),
  background: theme.palette.extra.backgroundGrey,
  borderRadius: 12,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  marginBottom: theme.spacing(2),
  marginLeft: 0,
  width: "100%",
  "& .MuiFormControlLabel-label": {
    fontWeight: 500,
    color: theme.palette.text.primary,
    fontSize: "1rem",
  },
}));

interface IPlayerEvents {
  teamId: string;
  trainingWeekList: any;
  attendance?: any;
  user?: any;
}

const PlayerEvents = injectIntl<IPlayerEvents>(
  ({ teamId, trainingWeekList, attendance, user, intl }) => {
    const { data: nextMatchData } = useQuery(NEXT_MATCH, {
      variables: {
        teamId,
      },
    });
    const [nextMatch, setNextMatch] = useState<any>();
    const [
      nextMatchAttendanceConfirmed,
      setNextMatchAttendanceConfirmed,
    ] = useState(false);

    const [attendMatch, { data: attendMatchData }] = useMutation(ATTEND_MATCH);

    const handleAttendMatch = async (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const nextState = event.target.checked;
      await attendMatch({
        variables: {
          matchId: nextMatch.id,
          playerId: user.playerLink,
          isComing: nextState,
        },
      });
    };

    useEffect(() => {
      if (nextMatchData) {
        setNextMatch(nextMatchData.nextMatch);

        // console.log("nextMatchData.playersConfirmed");
        // console.log(nextMatchData.nextMatch.playersConfirmed);
        const amIInTheList =
          nextMatchData.nextMatch &&
          nextMatchData.nextMatch.playersConfirmed &&
          nextMatchData.nextMatch.playersConfirmed.filter(
            (element: any) => element.id === user.playerLink
          );

        setNextMatchAttendanceConfirmed(
          amIInTheList && amIInTheList.length > 0
        );
      }
    }, [nextMatchData]);

    useEffect(() => {
      if (attendMatchData) {
        const amIInTheList =
          attendMatchData.attendMatch.playersConfirmed &&
          attendMatchData.attendMatch.playersConfirmed.filter(
            (element: any) => element.id === user.playerLink
          );
        // console.log("amIInTheList", amIInTheList);

        setNextMatchAttendanceConfirmed(amIInTheList.length > 0);
      }
    }, [attendMatchData]);

    return (
      <SectionWrapper>
        <CalendarComponent
          nextMatch={nextMatch}
          trainingWeekList={trainingWeekList}
        />
        {attendance && attendance.length > 0 && (
          <AttendanceSection>
            <AttendanceTitle>
              Attendance: ({attendance.length})
            </AttendanceTitle>
            {attendance.map((player: any) => (
              <PlayerRow
                container
                key={player.player.id}
              >
                <Grid item xs={6}>
                  <Typography.Subtitle1 style={{ fontWeight: 600 }}>
                    {player.player.name}&nbsp;{player.player.surname}
                  </Typography.Subtitle1>
                </Grid>
                <Grid item xs={6} style={{ textAlign: "right" }}>
                  {player.attendance ? (
                    <CheckedIcon style={{ color: "#2e7d32", fontSize: "1.5rem" }} />
                  ) : (
                    <UnCheckedIcon style={{ color: "#E64A19", fontSize: "1.5rem" }} />
                  )}
                </Grid>
              </PlayerRow>
            ))}
          </AttendanceSection>
        )}

        <NextMatchSection>
          <MatchHeader>
            <MatchHeaderTitle>
              {intl.formatMessage(messages.nextMatch)}
            </MatchHeaderTitle>
          </MatchHeader>
          {!nextMatch && (
            <MatchBody>
              <EmptyText>{intl.formatMessage(messages.noMatch)}</EmptyText>
            </MatchBody>
          )}
          {nextMatch && (
            <MatchBody>
              <div style={{ textAlign: "center", marginBottom: "16px" }}>
                <MatchDateBadge>
                  {moment(nextMatch.date).isSame(Date.now(), "day")
                    ? "Today"
                    : moment(nextMatch.date).format("dddd, DD-MM-YYYY")}
                  &nbsp;at {nextMatch.time}
                </MatchDateBadge>
              </div>

              <StyledFormControlLabel
                control={
                  <Switch
                    checked={nextMatchAttendanceConfirmed}
                    onChange={(event) => handleAttendMatch(event)}
                    color="primary"
                  />
                }
                label={intl.formatMessage(messages.confirmAttendance)}
              />
              <NextMatchCard match={nextMatch} />
            </MatchBody>
          )}
        </NextMatchSection>
      </SectionWrapper>
    );
  }
);

export default PlayerEvents;
